import { LegendKey } from "../../../../models/chart_model";
import { COST_UNIT } from "../../../../Units";
import { getTimeRange } from "../../../../pages/Dashboard/Exchange3/Chart";
import { monthNames } from "../../../../pages/Dashboard/PriceForcating/IexMonthly";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { compareLegends } from "./legendUtils";

export interface SnapshotItem {
  seriesName: string;
  displayName: string;
  value: number;
  /** Optional pre-formatted value from resolveValue. Compare overlay uses this when present. */
  formattedValue?: string;
  color: string;
  unit: string;
}

export interface TooltipSnapshot {
  dataIndex: number;
  xName: string;
  title: string;
  items: SnapshotItem[];
}

export interface SnapshotCtx {
  legends: LegendKey[];
  unit?: string;
  hidePrice?: boolean;
  isTimeSlot?: boolean;
  blockDurationMinutes?: number;
  isPriceForecast?: boolean;
  secondXDataKey?: string;
  debouncedData: any[];
  customLegendOrder?: string[];
  tooltipFilter?: (item: any, param: any) => boolean;
  resolveDisplayName?: (seriesName: string, dataPoint: any) => string;
  resolveValue?: (
    value: number,
    seriesName: string,
    dataPoint: any,
    legend: any,
  ) => string | null | undefined;
}

function resolveSuffix(
  seriesName: string,
  legends: LegendKey[],
  unit?: string,
): string {
  const legend = legends.find((l) => l.name === seriesName);
  if (legend?.unit !== undefined) return legend.unit;
  const suffixDict: Record<string, string> = {
    Price: COST_UNIT,
    Percent: "%",
    "%": "%",
    Cost: "Rs",
    Revenue: "Rs",
  };
  const foundKey = Object.keys(suffixDict).find((key) =>
    seriesName?.toString().includes(key),
  );
  return foundKey ? suffixDict[foundKey] : legend?.unit || unit || "";
}

export function extractTooltipSnapshot(
  params: any,
  ctx: SnapshotCtx,
): TooltipSnapshot | null {
  const {
    legends,
    unit,
    hidePrice,
    isTimeSlot,
    blockDurationMinutes,
    isPriceForecast,
    secondXDataKey,
    debouncedData,
    customLegendOrder,
    tooltipFilter,
    resolveDisplayName,
    resolveValue,
  } = ctx;

  const param = Array.isArray(params) ? params[0] : params;
  if (!param) return null;
  let items = Array.isArray(params) ? params : [params];
  if (tooltipFilter) items = items.filter((it) => tooltipFilter(it, param));

  const cleanName =
    typeof param.name === "string"
      ? normalizeDisplayLabel(param.name)
      : param.name;
  let title = cleanName;
  if (secondXDataKey && debouncedData[param.dataIndex]?.[secondXDataKey]) {
    title = isTimeSlot
      ? `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName} (${getTimeRange(parseInt(param.name), blockDurationMinutes)})`
      : `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName}`;
  } else if (isTimeSlot) {
    title = getTimeRange(parseInt(param.name), blockDurationMinutes) + ` (${param.name})`;
  }

  const sortedItems = [...items].sort((a, b) =>
    compareLegends(a.seriesName || "", b.seriesName || "", customLegendOrder),
  );
  const validItems = sortedItems.filter((item) => {
    if (item.seriesName?.startsWith("__")) return false;
    if (item.value === null || item.value === undefined) return false;
    const value = parseFloat(item.value);
    return !isNaN(value) && isFinite(value);
  });
  if (validItems.length === 0) return null;

  const now = new Date();
  const currentMonth = monthNames[now.getMonth()];
  const currentYear = now.getFullYear().toString();

  const snapItems: SnapshotItem[] = validItems.map((item) => {
    const year = item.seriesName?.trim();
    let displaySeriesName = item.seriesName;
    if (
      isPriceForecast &&
      item.name === currentMonth &&
      year === currentYear
    ) {
      displaySeriesName = `${currentYear} Forecast`;
    }
    let finalDisplay = normalizeDisplayLabel(
      hidePrice ? displaySeriesName.replace("Price", "") : displaySeriesName,
    );
    if (resolveDisplayName) {
      finalDisplay = resolveDisplayName(
        finalDisplay,
        debouncedData[param.dataIndex] ?? {},
      );
    }
    const rawValue = parseFloat(item.value);
    const dp = debouncedData[param.dataIndex] ?? {};
    const legend = legends.find((l) => l.name === item.seriesName);
    const formatted = resolveValue
      ? resolveValue(rawValue, item.seriesName, dp, legend)
      : null;
    return {
      seriesName: item.seriesName,
      displayName: finalDisplay,
      value: rawValue,
      formattedValue: formatted != null ? formatted : undefined,
      color: item.color,
      unit: resolveSuffix(item.seriesName, legends, unit),
    };
  });

  return {
    dataIndex: param.dataIndex,
    xName: param.name,
    title: String(title),
    items: snapItems,
  };
}
